import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Package, Tag, Sparkles, Gift, Info } from "lucide-react";

const ICONS = { order: Package, offer: Tag, product: Sparkles, reward: Gift, info: Info };
const LS_READ_KEY = "dyntra_notifications_read_v1";

const SEED_NOTIFICATIONS = [
  {
    id: "n1",
    type: "order",
    title: "Your last order shipped",
    body: "Order #DS-24012 is out for delivery — expected by tomorrow evening.",
    href: "/orders",
    time: "2h ago",
  },
  {
    id: "n2",
    type: "offer",
    title: "Bulk buy — extra 15% off ends tonight",
    body: "Save more on wholesale packs of 20+ before the deal expires.",
    href: "/offers",
    time: "5h ago",
  },
  {
    id: "n3",
    type: "product",
    title: "New arrival: Woodpecker AI Endo Motor",
    body: "Restock alert for the item you added to wishlist.",
    href: "/shop?q=endo+motor",
    time: "Yesterday",
  },
  {
    id: "n4",
    type: "reward",
    title: "You earned 120 Dyntra Coins",
    body: "Redeem your rewards on your next order at checkout.",
    href: "/rewards",
    time: "2 days ago",
  },
];

function readReadIds() {
  try {
    const raw = localStorage.getItem(LS_READ_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeReadIds(ids) {
  try {
    localStorage.setItem(LS_READ_KEY, JSON.stringify(ids));
  } catch { /* ignore */ }
}

export default function NotificationsMenu({ open, onOpenChange, triggerRef }) {
  const panelRef = useRef(null);
  const [readIds, setReadIds] = useState([]);

  useEffect(() => {
    setReadIds(readReadIds());
  }, []);

  const notifications = useMemo(() => SEED_NOTIFICATIONS, []);
  const unread = notifications.filter((n) => !readIds.includes(n.id));

  // Close on outside click or escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef?.current && !triggerRef.current.contains(e.target)
      ) {
        onOpenChange(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange, triggerRef]);

  const markRead = useCallback((id) => {
    setReadIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeReadIds(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    const all = notifications.map((n) => n.id);
    setReadIds(all);
    writeReadIds(all);
  }, [notifications]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
      data-testid="notifications-menu"
      className="absolute right-0 top-full mt-2 z-50 w-[360px] max-w-[calc(100vw-1.5rem)] rounded-2xl border border-gray-100 bg-white shadow-xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-[#F8F9FA]">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-[#4B2AAD]" />
          <p className="font-display text-sm font-bold text-gray-900">Notifications</p>
          {unread.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-[#F43F5E] text-white text-[10px] font-bold h-4 min-w-4 px-1.5">{unread.length}</span>
          )}
        </div>
        <button
          type="button"
          data-testid="notifications-mark-all"
          onClick={markAllRead}
          className="text-[11px] font-semibold text-[#4B2AAD] hover:underline flex items-center gap-1"
        >
          <CheckCheck className="h-3.5 w-3.5" /> Mark all read
        </button>
      </div>

      <ul className="max-h-[360px] overflow-y-auto thin-scroll divide-y divide-gray-100">
        {notifications.length === 0 && (
          <li className="p-6 text-center text-sm text-gray-500">You're all caught up.</li>
        )}
        {notifications.map((n) => {
          const Ico = ICONS[n.type] || Info;
          const isRead = readIds.includes(n.id);
          return (
            <li key={n.id}>
              <Link
                to={n.href}
                data-testid={`notification-${n.id}`}
                onClick={() => { markRead(n.id); onOpenChange(false); }}
                className={`flex gap-3 px-4 py-3 hover:bg-[#F8F9FA] transition-colors ${isRead ? "opacity-70" : ""}`}
              >
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${isRead ? "bg-gray-100" : "bg-[#F3EFFC]"}`}>
                  <Ico className={`h-4 w-4 ${isRead ? "text-gray-500" : "text-[#4B2AAD]"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold text-gray-900 line-clamp-1 ${isRead ? "" : ""}`}>{n.title}</p>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{n.body}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{n.time}</p>
                </div>
                {!isRead && <span className="mt-1 h-2 w-2 rounded-full bg-[#F43F5E] shrink-0" aria-label="Unread" />}
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-gray-100 p-3 bg-[#F8F9FA]">
        <Link
          to="/notifications"
          data-testid="notifications-view-all"
          onClick={() => onOpenChange(false)}
          className="block text-center text-sm font-semibold text-[#4B2AAD] hover:underline"
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}

export function useNotificationsUnread() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const read = readReadIds();
    setCount(SEED_NOTIFICATIONS.filter((n) => !read.includes(n.id)).length);
    const onStorage = (e) => {
      if (e.key === LS_READ_KEY) {
        const nextRead = readReadIds();
        setCount(SEED_NOTIFICATIONS.filter((n) => !nextRead.includes(n.id)).length);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return count;
}
